import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { CreateHealingRoomData, UpdateHealingRoomData } from 'api/healingRoom';
import {
  PaginationData,
  HealingRoom,
} from 'constants/interface';
import { RootState } from 'state';

const healingRoomEntityAdapter = createEntityAdapter<HealingRoom>();

export interface HealingRoomsState extends EntityState<HealingRoom> {
  isLoading: boolean
  remoteTotal: number
}

export const initialState: HealingRoomsState = healingRoomEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
});

export const createHealingRoomAction = createAsyncThunk(
  'healing-rooms/create',
  async (payload: CreateHealingRoomData) => {
    const healingRoomData = await apiClient.healingRoom.createHealingRoom(payload);
    return healingRoomData;
  },
);

export const updateHealingRoomAction = createAsyncThunk(
  'healing-rooms/update',
  async (payload: UpdateHealingRoomData) => {
    const healingRoomData = await apiClient.healingRoom.updateHealingRoom(payload);
    return healingRoomData;
  },
);

export const getHealingRoomAction = createAsyncThunk(
  'healing-rooms/get',
  async (payload: {
    healingRoomId: string,
  }) => {
    const { healingRoomId } = payload;
    const response = await apiClient.healingRoom.getHealingRoom(healingRoomId);

    return response;
  },
);

export const getHealingRoomListAction = createAsyncThunk(
  'healing-rooms/list',
  async (payload: PaginationData) => {
    const response = await apiClient
      .healingRoom
      .getHealingRoomList(payload);

    return response;
  },
);

export const healingRoomsSlice = createSlice({
  name: 'healing-rooms',
  initialState,
  reducers: {
    cleanHealingRoomsAction: healingRoomEntityAdapter.removeAll,
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getHealingRoomListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getHealingRoomListAction.fulfilled, (state, action) => {
      healingRoomEntityAdapter.removeAll(state);
      healingRoomEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getHealingRoomListAction.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  cleanHealingRoomsAction,
} = healingRoomsSlice.actions;

// Selectors

export const isLoadingHealingRoomsSelector = (state: RootState):
  boolean => state.healingRooms.isLoading;

export const healingRoomsEntitiesSelector = (state: RootState):
  Dictionary<HealingRoom> => state.healingRooms.entities;

export const healingRoomsIdsSelector = (state: RootState):
  EntityId[] => state.healingRooms.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.healingRooms.remoteTotal;
